import React, { useEffect, useRef, useState } from 'react';
import type { SavedSignature } from '../types/fields';

interface SavedSignatureCardProps {
  signature: SavedSignature;
  canPlace?: boolean;
  onPlace: (signature: SavedSignature) => void;
  onRename: (id: string, label: string) => void;
  onDelete: (id: string) => void;
}

export const SavedSignatureCard: React.FC<SavedSignatureCardProps> = ({
  signature,
  canPlace = true,
  onPlace,
  onRename,
  onDelete,
}) => {
  // Edit the label in local state and only commit on blur/Enter. Committing on every
  // keystroke re-serialized the whole base64 library to localStorage per character
  // (jank + quota risk) and made the field impossible to clear and retype.
  const [labelDraft, setLabelDraft] = useState(signature.label);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const isEscapingRef = useRef(false);
  useEffect(() => {
    setLabelDraft(signature.label);
  }, [signature.label]);

  const commitLabel = () => {
    if (isEscapingRef.current) {
      isEscapingRef.current = false;
      setLabelDraft(signature.label);
      return;
    }
    const trimmed = labelDraft.trim();
    if (trimmed && trimmed !== signature.label) {
      onRename(signature.id, trimmed);
    } else {
      setLabelDraft(signature.label); // revert empty/unchanged edits
    }
  };

  return (
    <article
      className="saved-signature-card"
      // Fix A11Y-3: was role="group" on a clickable/keyboard element — ATs need role="button"
      // so they announce it as interactive ("button") rather than a generic grouping landmark.
      role="button"
      aria-label={`Place saved signature: ${signature.label}`}
      onClick={() => {
        if (canPlace) onPlace(signature);
      }}
      onKeyDown={(e) => {
        if (!canPlace) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onPlace(signature);
        }
      }}
      tabIndex={canPlace ? 0 : -1}
      aria-disabled={!canPlace}
    >
      <div className="saved-signature-preview">
        <img src={signature.dataUrl} alt={signature.label} draggable={false} />
      </div>
      <input
        value={labelDraft}
        onClick={(e) => e.stopPropagation()}
        onChange={(event) => setLabelDraft(event.target.value)}
        onBlur={commitLabel}
        onKeyDown={(event) => {
          event.stopPropagation();
          if (event.key === 'Enter') {
            event.currentTarget.blur();
          } else if (event.key === 'Escape') {
            isEscapingRef.current = true;
            event.currentTarget.blur();
          }
        }}
        className="saved-signature-name"
        aria-label="Edit signature label"
      />
      <div
        className="saved-signature-actions"
        role="toolbar"
        aria-label="Signature actions"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          disabled={!canPlace}
          onClick={() => onPlace(signature)}
          className="saved-signature-place"
          title={canPlace ? undefined : 'Upload a PDF first'}
          aria-label={`Place ${signature.label}`}
        >
          {canPlace ? 'Place on PDF' : 'Need PDF'}
        </button>
        {confirmDelete ? (
          <>
            <button
              type="button"
              onClick={() => {
                onDelete(signature.id);
                setConfirmDelete(false);
              }}
              className="saved-signature-delete saved-signature-delete-confirm"
              aria-label={`Confirm delete ${signature.label}`}
            >
              Confirm delete
            </button>
            <button
              type="button"
              onClick={() => setConfirmDelete(false)}
              className="saved-signature-delete-cancel"
              aria-label="Cancel delete"
            >
              Cancel
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={() => setConfirmDelete(true)}
            className="saved-signature-delete"
            aria-label={`Delete ${signature.label}`}
          >
            Delete
          </button>
        )}
      </div>
      <div className="saved-signature-date" aria-label={`Saved on ${new Date(signature.savedAt).toLocaleDateString()}`}>
        Saved {new Date(signature.savedAt).toLocaleDateString()}
      </div>
    </article>
  );
};
